"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/providers/AuthProvider";
import { logger } from "@/utils/logger";
import type { CreditsResponse } from "@/app/actions/credits";

// Re-export types for convenience
export type { CreditsData, CreditsResponse } from "@/app/actions/credits";

/**
 * Custom error class for rate limit errors with Retry-After information
 */
class RateLimitError extends Error {
  constructor(
    message: string,
    public retryAfter?: number,
    public statusCode: number = 429
  ) {
    super(message);
    this.name = "RateLimitError";
  }
}

/**
 * Custom error class for unauthorized errors (401)
 * These indicate session expiration and should not be retried
 */
class UnauthorizedError extends Error {
  constructor(
    message: string,
    public statusCode: number = 401
  ) {
    super(message);
    this.name = "UnauthorizedError";
  }
}

/**
 * Custom retry function that handles 429 and 401 errors
 * Never retry on rate limit errors or unauthorized errors
 */
function retryWithRateLimitHandling(
  failureCount: number,
  error: unknown
): boolean {
  // Never retry on rate limit errors - respect the Retry-After header
  if (error instanceof RateLimitError) {
    logger.warn("Rate limit error in credits fetch, not retrying", {
      component: "useCredits",
      failureCount,
      retryAfter: error.retryAfter,
    });
    return false;
  }

  // Never retry on 401 unauthorized errors - session has expired
  // This prevents spam when server logs out user but client session is still truthy
  if (error instanceof UnauthorizedError) {
    logger.debug(
      "Unauthorized error in credits fetch, not retrying (session expired)",
      {
        component: "useCredits",
        failureCount,
        statusCode: error.statusCode,
      }
    );
    return false;
  }

  // Also check error message for 401 status codes (fallback for errors thrown before UnauthorizedError was added)
  if (error instanceof Error && error.message.includes("401")) {
    logger.debug(
      "401 error detected in credits fetch, not retrying (session expired)",
      {
        component: "useCredits",
        failureCount,
        errorMessage: error.message,
      }
    );
    return false;
  }

  // Don't retry if we've already retried too many times
  const maxRetries = 3;
  if (failureCount >= maxRetries) {
    return false;
  }

  // For other errors, use default retry logic (retry up to 2 times)
  return failureCount < 2;
}

/**
 * Custom retry delay function that respects Retry-After headers for 429 errors
 */
function retryDelayWithRateLimit(attemptIndex: number, error: unknown): number {
  // If it's a rate limit error with Retry-After, use that
  if (error instanceof RateLimitError && error.retryAfter) {
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 1000;
    return error.retryAfter + jitter;
  }

  // For other errors, use exponential backoff
  return Math.min(1000 * 2 ** attemptIndex, 10000);
}

/**
 * Centralized credits hook to eliminate redundant API calls
 * Uses Server Actions with SSR initial data support
 * Falls back to API route for backward compatibility
 *
 * @param initialData - Optional initial credits data from server component (SSR)
 */
export function useCredits(initialData?: CreditsResponse) {
  const { session, loading: authLoading, isOAuthFlow } = useAuth();
  const queryClient = useQueryClient();
  const creditsUserId = session?.user?.id ?? "anonymous";

  // Track when session is ready (session exists and auth is not loading)
  // Server Actions handle cookie management automatically
  const sessionReady = !!session && !authLoading;

  const fetchCredits = useCallback(async () => {
    logger.debug("Fetching credits via useCredits hook (API route)", {
      component: "useCredits",
      hasSession: !!session,
      authLoading,
      sessionReady,
      hasInitialData: !!initialData,
    });

    // Use API route for client-side calls (Server Actions should only be called from Server Components)
    // cache: "no-store" ensures we never get stale balance after purchase/earn (browser would otherwise use Cache-Control max-age=30)
    const res = await fetch("/api/credits", {
      credentials: "include",
      cache: "no-store",
    });

    if (!res.ok) {
      // Handle 401 unauthorized errors - session expired
      // Don't retry these as they indicate the session is invalid
      if (res.status === 401) {
        logger.warn("Session expired when fetching credits (401)", {
          component: "useCredits",
          status: res.status,
          hasSession: !!session,
          authLoading,
          sessionReady,
        });

        throw new UnauthorizedError(
          "Session expired. Please sign in again.",
          res.status
        );
      }

      // Handle 429 rate limit errors with Retry-After header
      if (res.status === 429) {
        const retryAfterHeader = res.headers.get("Retry-After");
        const retryAfter = retryAfterHeader
          ? parseInt(retryAfterHeader, 10) * 1000
          : undefined;

        const errorMessage =
          retryAfter && retryAfter > 0
            ? `Too many requests. Please try again in ${Math.ceil(retryAfter / 1000)} seconds.`
            : "Too many requests. Please try again in a moment.";

        logger.warn("Rate limit error when fetching credits", {
          component: "useCredits",
          status: res.status,
          retryAfter: retryAfter ? `${retryAfter}ms` : "unknown",
          hasSession: !!session,
          authLoading,
          sessionReady,
        });

        throw new RateLimitError(errorMessage, retryAfter, res.status);
      }

      // For other errors, create a standard error with detailed context
      const errorMessage = `Failed to fetch credits: ${res.status} ${res.statusText || ""}`;

      logger.error("Error fetching credits", {
        component: "useCredits",
        status: res.status,
        statusText: res.statusText || "unknown",
        hasSession: !!session,
        authLoading,
        sessionReady,
      });

      throw new Error(errorMessage);
    }

    // Success case - parse JSON response
    return res.json();
  }, [authLoading, initialData, session, sessionReady]);

  const {
    data: creditsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<CreditsResponse, Error>({
    queryKey: ["credits", creditsUserId],
    queryFn: fetchCredits,
    enabled: sessionReady && !isOAuthFlow, // Don't fetch during OAuth flow
    // Use placeholderData for SSR initial data instead of initialData
    // placeholderData doesn't mark data as "fresh", allowing proper refetches on invalidation
    // This ensures credits update immediately when invalidated (e.g., after purchase)
    // The placeholder shows instantly, then real data loads seamlessly
    placeholderData: initialData, // Use SSR initial data as placeholder if provided
    staleTime: 60 * 1000, // Keep credits warm for 60s to reduce route churn
    gcTime: 5 * 60 * 1000, // 5 minutes cache time (formerly cacheTime)
    refetchOnWindowFocus: false, // Disable refetch on window focus for performance
    refetchOnMount: false, // Disable auto-refetch on mount - use invalidateCredits() explicitly to prevent spam
    refetchOnReconnect: false,
    // Custom retry logic for rate limit errors
    retry: retryWithRateLimitHandling,
    retryDelay: retryDelayWithRateLimit,
  });

  // Helper to invalidate credits cache after transactions
  // React Query automatically deduplicates queries, so we don't need global state
  // Just invalidate and let React Query handle the rest
  const invalidateCredits = useCallback(async () => {
    const queryKey = ["credits", creditsUserId] as const;

    // Mark every observer stale, then force a no-store fetch and wait for it.
    // This matters after checkout returns: stale cached 0-credit data must not
    // briefly drive the create page back into an insufficient-credit state.
    await queryClient.invalidateQueries({
      queryKey,
      refetchType: "none",
    });

    return await queryClient.fetchQuery({
      queryKey,
      queryFn: fetchCredits,
      staleTime: 0,
    });
  }, [creditsUserId, fetchCredits, queryClient]);

  // Helper to get current balance without triggering a fetch
  const getCurrentBalance = useCallback((): number | null => {
    const cached = queryClient.getQueryData<CreditsResponse>([
      "credits",
      creditsUserId,
    ]);
    // Return null if credits haven't loaded yet (distinguish from 0 balance)
    if (!cached && isLoading) {
      return null;
    }
    return cached?.data?.balance ?? 0;
  }, [creditsUserId, queryClient, isLoading]);

  // Helper to check if user has sufficient credits
  // Returns: true if sufficient, false if insufficient, null if still loading/fetching
  const hasSufficientCredits = useCallback(
    (required: number = 1): boolean | null => {
      // If credits are still loading or being refetched, return null to indicate unknown state
      // This prevents false negatives when credits haven't loaded yet or are being refreshed
      // (e.g., after a credit purchase)
      if (isLoading || isFetching || !sessionReady) {
        return null;
      }

      const balance = getCurrentBalance();
      // If balance is null, we're still loading
      if (balance === null) {
        return null;
      }

      return balance >= required;
    },
    [isLoading, isFetching, sessionReady, getCurrentBalance]
  );

  return {
    // Core data
    // Return null when loading to distinguish from actual 0 balance
    balance:
      isLoading || !creditsData ? null : (creditsData.data?.balance ?? 0),
    transactions: creditsData?.data?.transactions ?? [],

    // Loading states
    isLoading,
    isFetching, // Expose isFetching to track when a refetch is in progress
    error,
    sessionReady, // Expose sessionReady for components to check if session is fully ready

    // Actions
    refetch,
    invalidateCredits,

    // Utilities
    getCurrentBalance,
    hasSufficientCredits,
  };
}
